import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import { fetchOptions } from "PFApp/audit_planner/parts/modals/parts/exception_modal_content.utils";
import { ProfileItem } from "PFApp/components/profile_item";
import { Divider } from "PFComponents/divider";
import { ReadOnlyField } from "PFComponents/read_only_field";
import { Select } from "PFComponents/select";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { getProfileName } from "PFCore/helpers/profile";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { Activity, AuditPlannerExceptionsDictionary, AuditRole, BasicProfile, Shortlist } from "PFTypes";
import { Option } from "PFTypes/options";
import { useTranslation } from "react-i18next";

import { ReasonForException } from "./reason_for_exception";
import { RoleInformationRow } from "./role_information_row";

const getGradeValue = (role: AuditRole | Activity) => {
  // @ts-ignore-next-line
  const roleCustomFields = "customFields" in role ? role.customFields : role.custom_fields;
  const roleGrade = "grade" in role ? role.grade : undefined;
  if (roleGrade) {
    return roleGrade;
  }
  const gradeCustomField = roleCustomFields?.find(({ type: { name } }) => name === "grade");
  if (!gradeCustomField) {
    return "-";
  }
  const gradeCustomFieldValues = compact(
    (gradeCustomField.values ?? [])?.map((gradeValue) => gradeValue.value || gradeValue.text)
  );
  if (isEmpty(gradeCustomFieldValues)) {
    return "-";
  }
  return gradeCustomFieldValues.sort().join(", ");
};

export type ExceptionModalContentProps = {
  exception?: AuditPlannerExceptionsDictionary;
  role: AuditRole | Activity;
  edit?: boolean;
  isEditMode?: boolean;
  requester?: BasicProfile;
  requestReason?: string;
  shortlistItem: Shortlist;
  approver: BasicProfile | null;
  onApproverChange: (approver: BasicProfile) => void;
  note?: string;
  onNoteChange?: (note: string) => void;
};

export const ExceptionModalContent = ({
  shortlistItem,
  exception,
  requestReason,
  requester,
  role,
  isEditMode,
  approver,
  onApproverChange,
  note,
  onNoteChange
}: ExceptionModalContentProps) => {
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation("auditPlanner");

  const { profile } = shortlistItem;

  const grade = getGradeValue(role);

  return (
    <>
      <RoleInformationRow
        data={[
          {
            label: t("parts.headers.grade"),
            value: grade
          },
          {
            label: t("parts.headers.resource"),
            value: getProfileName(profile)
          },
          {
            label: t("parts.headers.raisedBy"),
            value: getProfileName(requester || currentProfile)
          }
        ]}
      />
      <Divider />
      {exception && (
        <ReadOnlyField
          label={t("modals.reasonForException")}
          value={<ReasonForException profile={profile} exception={exception} />}
        />
      )}
      {isEditMode && !requester ? (
        <InputFieldSet
          inputType="textarea"
          placeholder={t("modals.addANote")}
          value={note}
          onChange={onNoteChange}
          maxLength={280}
          minVisibleMaxLengthFactor={2}
          maxHeight={200}
        />
      ) : (
        <ReadOnlyField label={t("modals.note")} value={requestReason || "-"} />
      )}
      <Divider />
      {isEditMode ? (
        <Select<BasicProfile>
          value={approver?.id}
          onChange={(option: Option<BasicProfile>) => onApproverChange(option.original)}
          fetchOptions={fetchOptions}
          label={t("modals.requestApprovalFrom")}
          parseOptions={(options: Option<BasicProfile>[]) =>
            options.filter(({ id }) => currentProfile.id !== id)
          }
        />
      ) : (
        approver && <ProfileItem profile={approver} />
      )}
    </>
  );
};
